import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown, Dna } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
  RectangularOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useInfertility } from "../InfertilityContext";
import type { InfertilityStackParamList } from "../InfertilityNavigator";

// "art_needed" = "No, but I need ART to screen embryos" (ART = Assisted Reproductive Technology)
type DiagnosisOption = "yes" | "no" | "art_needed";

export function InfertilityDiagnosisScreen() {
  const t = useTranslations();
  const { state, updateState } = useInfertility();
  const selection: DiagnosisOption | null =
    (state.hasDiagnosis as DiagnosisOption | null | undefined) ?? null;
  const navigation = useNavigation<NavigationProp<InfertilityStackParamList>>();

  const handleSave = () => {
    if (selection === "yes") {
      navigation.navigate("InfertilityCause");
    } else {
      // "no" or "art_needed" skip to partner diagnosis
      navigation.navigate("PartnerInfertilityDiagnosis");
    }
  };

  const handleSelection = (value: DiagnosisOption) => {
    // If changing to "no" or "art_needed", clear causes and mark for deletion
    if (value === "no" || value === "art_needed") {
      updateState({
        hasDiagnosis: value,
        causes: [],
        causeOther: null,
      });
    } else {
      updateState({ hasDiagnosis: value });
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.fertility.infertility.diagnosis_title")}
      subtitle={t("med_history.fertility.infertility.diagnosis_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!selection}
      saveLabel={t("med_history.fertility.continue")}
    >
      <View style={styles.optionsContainer}>
        <View style={styles.squareOptionsRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleSelection("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleSelection("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>

        <RectangularOptionButton
          icon={Dna}
          label={t("med_history.fertility.infertility.diagnosis_art_needed")}
          isSelected={selection === "art_needed"}
          onPress={() => handleSelection("art_needed")}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    width: "100%",
    gap: 12,
    marginBottom: 40,
  },
  squareOptionsRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
});

export default InfertilityDiagnosisScreen;
